import { Button, TextField } from "@mui/material";
import { FormikProvider, useFormik } from "formik";
import { Film } from "lucide-react";
import React, { useEffect, useId, useRef } from "react";
import { useTranslation } from "react-i18next";
import * as Yup from "yup";
import {
  ExperienceErrorText,
  ExperienceHeader,
  ExperienceMediaCard,
  ExperienceOptionalLabel,
  ExperienceShell,
  ExperienceUploadZone,
  type ExperienceMomentVariant,
  getExperienceFieldSx,
  getFloatingActionSx,
  isSeasonMomentVariant,
} from "./ExperienceUI";

interface VideoData {
  videoUrl?: string;
  description?: string;
}

interface VideoExperienceProps {
  darkMode: boolean;
  data?: VideoData;
  momentVariant?: ExperienceMomentVariant;
  onDataChange?: (data: VideoData) => void;
}

const getValidationSchema = (t: any, isSeason: boolean) =>
  isSeason
    ? Yup.object({
        videoUrl: Yup.string().required(t("video_required_season")),
        description: Yup.string().nullable(),
      })
    : Yup.object({
        videoUrl: Yup.string().required(t("video_required")),
        description: Yup.string()
          .required(t("description_required"))
          .min(10, t("description_min_length")),
      });

const VideoExperience: React.FC<VideoExperienceProps> = ({
  darkMode,
  data,
  momentVariant = "standard",
  onDataChange,
}) => {
  const { t } = useTranslation("taascard");
  const isSeason = isSeasonMomentVariant(momentVariant);
  const validationSchema = getValidationSchema(t, isSeason);
  const isInternalUpdateRef = useRef(false);
  const instanceId = useId().replace(/:/g, "");
  const uploadId = `video-upload-${instanceId}`;
  const fieldSx = getExperienceFieldSx(darkMode);

  const formik = useFormik<VideoData>({
    initialValues: {
      videoUrl: data?.videoUrl ?? (isSeason ? "" : t("dummy_video_url")),
      description: data?.description ?? (isSeason ? "" : t("dummy_video_description")),
    },
    enableReinitialize: true,
    validationSchema,
    validateOnChange: false,
    validateOnBlur: !isSeason,
    onSubmit: () => {},
  });

  useEffect(() => {
    if (data && !isInternalUpdateRef.current) {
      const newValues: VideoData = {
        videoUrl: data.videoUrl ?? (isSeason ? "" : t("dummy_video_url")),
        description: data.description ?? (isSeason ? "" : t("dummy_video_description")),
      };
      const hasChanged = Object.keys(newValues).some(
        (key) => formik.values[key as keyof VideoData] !== newValues[key as keyof VideoData]
      );
      if (hasChanged) {
        formik.setValues(newValues);
      }
    }
    isInternalUpdateRef.current = false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  useEffect(() => {
    if (onDataChange) {
      const timeoutId = setTimeout(() => {
        isInternalUpdateRef.current = true;
        onDataChange({
          videoUrl: formik.values.videoUrl,
          description: formik.values.description,
        });
      }, 0);

      return () => clearTimeout(timeoutId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values]);

  const handleFileSelect = (files: FileList | null) => {
    if (files && files.length > 0) {
      const file = files[0];
      const reader = new FileReader();
      reader.onloadend = () => {
        const result = reader.result as string;
        formik.setFieldValue("videoUrl", result);
        formik.setFieldError("videoUrl", undefined);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleRemoveVideo = () => {
    formik.setFieldValue("videoUrl", "");
    formik.setFieldTouched("videoUrl", true);
  };

  return (
    <FormikProvider value={formik}>
      <ExperienceShell>
        {!isSeason && (
          <ExperienceHeader
            darkMode={darkMode}
            icon={Film}
            title={t("video", { ns: "talent", defaultValue: "Video" })}
            subtitle={t("video_header_subtitle")}
          />
        )}

        {formik.values.videoUrl ? (
          <ExperienceMediaCard
            darkMode={darkMode}
            actions={
              <Button
                variant="contained"
                onClick={handleRemoveVideo}
                size="small"
                sx={getFloatingActionSx(darkMode)}
              >
                {t("remove_video")}
              </Button>
            }
          >
            <video
              controls
              style={{
                width: "100%",
                maxHeight: "400px",
                objectFit: "contain",
                display: "block",
              }}
            >
              <source src={formik.values.videoUrl} />
              {t("video_browser_not_supported")}
            </video>
          </ExperienceMediaCard>
        ) : (
          <ExperienceUploadZone
            darkMode={darkMode}
            hasError={Boolean(formik.touched.videoUrl && formik.errors.videoUrl)}
            icon={Film}
            title={t("upload_video")}
            subtitle={t("drag_and_drop_video_to_upload")}
            onFileSelect={handleFileSelect}
            accept="video/*"
            inputId={uploadId}
            buttonLabel={t("choose_video_file")}
            minHeight={200}
          />
        )}
        {formik.touched.videoUrl && formik.errors.videoUrl && (
          <ExperienceErrorText darkMode={darkMode} sx={{ mt: -1.5 }}>
            {formik.errors.videoUrl}
          </ExperienceErrorText>
        )}

        <TextField
          label={
            <>
              {t("description")}
              {isSeason && <ExperienceOptionalLabel darkMode={darkMode} />}
            </>
          }
          name="description"
          value={formik.values.description}
          onChange={(e) => {
            formik.handleChange(e);
            formik.setFieldError("description", undefined);
          }}
          onBlur={formik.handleBlur}
          fullWidth
          multiline
          rows={4}
          placeholder={t("video_description_placeholder")}
          error={Boolean(formik.touched.description && formik.errors.description)}
          helperText={
            formik.touched.description && formik.errors.description
              ? formik.errors.description
              : isSeason
                ? t("video_description_helper_season")
                : " "
          }
          sx={fieldSx}
        />
      </ExperienceShell>
    </FormikProvider>
  );
};

export default VideoExperience;
